import { Collapse, Grid, IconButton, ToggleButton, Box, Theme } from '@mui/material';
import { FC, useState } from 'react';
import InfoIcon from '@mui/icons-material/Info';
import HelperModal from '@acorn-portal/shared/src/components/modal/HelperModal';
import SelectFormField, { IOptions } from '@acorn-portal/shared/src/components/inputs/select';
import FormField from '@acorn-portal/shared/src/components/inputs/FormField';
import { Product } from '@acorn-portal/shared/src/services/interface/Shared';
interface IModal {
	title: string;
	description: string;
}

interface IProps {
	onChange: (value: string) => void;
	name?: string;
	id?: string;
	error?: boolean;
	helperText?: string | false;
	value?: unknown;
	label: string;
	modal?: IModal;
	options: IOptions[];
	product?: Product;
}

const getToggleButtonStyles = ({ theme, error }: { theme: Theme; error?: boolean }) => {
	const company = import.meta.env.VITE_AUTH_REALM?.toUpperCase();
	const baseStyles = {
		height: '4rem',
		borderColor: error ? 'error.main' : theme.palette.common.black,
	};

	const getSelectedStyles = () => {
		if (company === 'ACORN') {
			return {
				backgroundColor: theme.palette.secondary.main,
				color: theme.palette.common.white,
				border: 'unset',
				'&:hover': {
					color: theme.palette.common.white,
					backgroundColor: theme.palette.secondary.light,
				},
			};
		}

		if (company === 'MOTORCADE') {
			return {
				backgroundColor: theme.palette.primary.main,
				color: theme.palette.common.white,
				'&:hover': {
					color: theme.palette.primary.main,
					backgroundColor: theme.palette.primary.light,
				},
			};
		}

		return {
			backgroundColor: theme.palette.primary.main,
			color: theme.palette.common.white,
			'&:hover': {
				color: theme.palette.common.white,
				backgroundColor: theme.palette.primary.light,
			},
		};
	};

	return {
		...baseStyles,
		'&.Mui-selected': getSelectedStyles(),
	};
};

const ToggleButtonFormField: FC<IProps> = ({
	error,
	helperText,
	id,
	label,
	modal,
	value,
	name,
	options,
	onChange,
	product,
}) => {
	const [open, toggleModal] = useState(false);

	let buttons = options?.length > 0 && options.filter((x) => x.isButton && !x.hidden);

	if (product && product === Product.TAXI) {
		buttons = options?.length > 0 && options.filter((x) => !x.hidden && x.filterBy === 'TX');
	}

	const getSelectedButton = () => {
		if (!value && value === '') return '';
		const isAvailable = buttons && buttons.filter((x) => x.value === value).length > 0;
		return isAvailable ? value : 'more';
	};

	const [selectedButton, setSelectedButton] = useState(getSelectedButton());
	const dropDownOptions = options?.length > 0 && options.filter((x) => !x.isButton && !x.hidden);

	const handleButtonSelected = (selectedOption: string) => {
		setSelectedButton(selectedOption);
		onChange(selectedOption);
	};

	const onMoreSelected = () => {
		setSelectedButton('more');
		onChange('');
	};

	const handleDropDownSelect = (selectedOption: string) => {
		onChange(selectedOption);
	};

	return (
		<>
			<FormField error={error} helperText={helperText} id={id} label={label}>
				<Grid container direction='row'>
					<Grid size={modal ? 11 : 12}>
						<Grid container direction='row' spacing={1}>
							{buttons &&
								buttons.map((button) => (
									<Grid key={`option-${button?.value}`} size={6}>
										<ToggleButton
											color='primary'
											sx={(theme) => getToggleButtonStyles({ theme, error })}
											fullWidth
											value={button.value as string}
											selected={button.value === selectedButton}
											onChange={() => handleButtonSelected(button.value as string)}
										>
											{button.label as string}
										</ToggleButton>
									</Grid>
								))}
							{dropDownOptions && dropDownOptions.length > 0 && product !== Product.TAXI && (
								<Grid size={6}>
									<ToggleButton
										color='primary'
										sx={(theme) => getToggleButtonStyles({ theme, error })}
										fullWidth
										value='more'
										selected={selectedButton === 'more'}
										onChange={() => onMoreSelected()}
									>
										More
									</ToggleButton>
								</Grid>
							)}
						</Grid>
					</Grid>
					{modal && (
						<Grid size={1}>
							<IconButton
								color='icon'
								aria-label={`${id ?? ''}-more-info`}
								component='span'
								sx={{ my: 1, px: 1 }}
								onClick={() => toggleModal(true)}
							>
								<InfoIcon />
							</IconButton>
						</Grid>
					)}
				</Grid>
			</FormField>
			{modal && <HelperModal onClose={() => toggleModal(false)} open={open} {...modal} />}
			<Collapse
				in={selectedButton === 'more' && dropDownOptions && dropDownOptions.length > 0 && product !== Product.TAXI}
			>
				<Box
					sx={(theme) => ({
						backgroundColor: theme.palette.grey[300],
						borderRadius: 2,
						pt: 3,
						mb: 3,
						'& .MuiOutlinedInput-root': {
							backgroundColor: theme.palette.background.paper,
						},
						[theme.breakpoints.down('md')]: {
							px: 1,
						},
					})}
				>
					<SelectFormField
						error={error}
						helperText={helperText}
						label='Please Select Option'
						name={name}
						value={selectedButton === 'more' ? value : ''}
						id={id}
						onChange={handleDropDownSelect}
						options={dropDownOptions || []}
					/>
				</Box>
			</Collapse>
		</>
	);
};

export default ToggleButtonFormField;
